import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Position,
  Menu,
  HTMLSelect,
  Slider,
  Popover,
  Dialog,
  Classes,
  InputGroup,
} from "@blueprintjs/core";
import JSZip from "jszip";
import { downloadFile } from "polotno/utils/download";
import * as unit from "polotno/utils/unit";
import { t } from "polotno/utils/l10n";
import axios from 'axios';
import { useSelector } from "react-redux";
import { FaCrown, FaExclamationTriangle } from 'react-icons/fa';
import { useDispatch } from "react-redux";
import { loginModal } from "../Redux/authAction";
import API from "../config/api"; // Import API configuration

export const DownloadButton = observer(({ store }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [saving, setSaving] = React.useState(false);
  const [quality, setQuality] = React.useState(1);
  const [pageSizeModifier, setPageSizeModifier] = React.useState(1);
  const [fps, setFPS] = React.useState(10);
  const [type, setType] = React.useState("png");
  const [fileName, setFileName] = React.useState(""); // State to store custom file name
  const [imagePaths, setImagePaths] = React.useState([]);
  const [showPopup, setShowPopup] = React.useState(false);
  
  // useSelector calls moved to the top of the component
  const isAuthenticated = useSelector(
    (state) => state.authReducer.isAuthenticated
  );
  const userID = useSelector((state) => state.authReducer.uid);
  const packageid = useSelector((state) => state.authReducer.packageid);
  const temp_pkgid = useSelector((state) => state.authReducer.temp_pkgid);
  const temp_id = useSelector((state) => state.authReducer.temp_id);
  const email = useSelector((state) => state.authReducer.email);

  console.log("isAuthenticated", isAuthenticated);

  React.useEffect(() => {
    const updateFileName = () => {
      setFileName(getName());
    };

    const checkForChanges = () => {
      if (
        store.pages.length !== 0 ||
        (store.pages[0] && store.pages[0].children.length !== 0)
      ) {
        updateFileName();
      }
    };

    const interval = setInterval(checkForChanges, 1000);

    updateFileName();

    return () => {
      clearInterval(interval);
    };
  }, [store]);

  const getName = () => {
    const texts = [];
    store.pages.forEach((p) => {
      p.children.forEach((c) => {
        if (c.type === "text") {
          texts.push(c.text);
        }
      });
    });

    const allWords = texts.join(" ").split(" ");
    const words = allWords.slice(0, 6);
    return words.join(" ").replace(/\s/g, "-").toLowerCase() || "Filename";
  };

  const [showPremiumPopup, setShowPremiumPopup] = React.useState(false);
  const [showOneTimePlanPopup, setShowOneTimePlanPopup] = React.useState(false); // State for one-time plan popup
  const [showExpiredPackagePopUp, setShowExpiredPackagePopUp] = React.useState(false); // State for one-time plan popup


  const handleDownload = async () => {
    console.log(
      "user Id:", userID,
      "user package id", packageid,
      "template Package id:", temp_pkgid,
      "template id : ", temp_id
    );

    // Check if the user is allowed to download based on package levels
    if (temp_pkgid == 4) {
      try {
        // Make a POST request to check the subscription, passing userID via FormData
        const formData = new FormData();
        formData.append("userid", userID);
        formData.append("tempid", temp_id);

        console.log("FormData:", formData);
        const subscriptionResponse = await fetch(API.getUrl(API.ENDPOINTS.GET_USER_TEMPLATE_SUBSCRIPTION), {
          method: 'POST',
          body: formData,
        });

        if (!subscriptionResponse.ok) {
          throw new Error("Failed to fetch subscription details");
        }

        const result = await subscriptionResponse.json();

        if (result.status === false) {
          setShowOneTimePlanPopup(true); // Show one-time plan popup if user hasn't subscribed
          return;
        }

      } catch (error) {
        console.error("Error fetching subscription or template details:", error);
        return; // Stop execution if any API call fails
      }
    } else {
      if (
        (packageid == 1 && temp_pkgid > 1) ||
        (packageid == 2 && temp_pkgid > 2) ||
        (packageid == 3 && temp_pkgid > 3)
      ) {
        setShowPremiumPopup(true);
        return; // Stop further execution
      }
      else{
        if(packageid == 6 && temp_pkgid > 1 || packageid == 7 && temp_pkgid > 1){
          setShowExpiredPackagePopUp(true);
          return;
        }
      }
    }

    // If the user has the right package level, proceed with the download
    setSaving(true);
    try {
      const finalFileName = fileName.trim() !== "" ? fileName.trim() : getName();

      if (type === "pdf") {
        await store.saveAsPDF({
          fileName: finalFileName + ".pdf",
          dpi: store.dpi / pageSizeModifier,
          pixelRatio: 2 * quality,
        });
      } else if (type === "html") {
        await store.saveAsHTML({
          fileName: finalFileName + ".html",
        });
      } else if (type === "gif") {
        await store.saveAsGIF({
          fileName: finalFileName + ".gif",
          pixelRatio: quality,
          fps,
        });
      } else if (type === "print") {
        await store.saveAsPDF({
          fileName: finalFileName + ".pdf",
          dpi: store.dpi / pageSizeModifier,
          pixelRatio: 2 * quality,
        });
      } else {
        if (store.pages.length < 3) {
          store.pages.forEach((page, index) => {
            const indexString = store.pages.length > 1 ? "-" + (index + 1) : "";
            store.saveAsImage({
              pageId: page.id,
              pixelRatio: quality,
              mimeType: "image/" + type,
              fileName: finalFileName + indexString + "." + type,
            });
          });
        } else {
          const zip = new JSZip();
          for (const page of store.pages) {
            const index = store.pages.indexOf(page);
            const indexString = store.pages.length > 1 ? "-" + (index + 1) : "";

            const url = await store.toDataURL({
              pageId: page.id,
              pixelRatio: quality,
              mimeType: "image/" + type,
            });
            const base64Data = url.replace(
              /^data:image\/(png|jpeg);base64,/,
              ""
            );
            zip.file(finalFileName + indexString + "." + type, base64Data, {
              base64: true,
            });
          }

          const content = await zip.generateAsync({ type: "base64" });
          const result = "data:application/zip;base64," + content;
          console.log(content);
          downloadFile(result, finalFileName + ".zip");
        }
      }
    } catch (e) {
      console.error("Something went wrong. Please try again.", e);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      const finalFileName = fileName.trim() !== "" ? fileName.trim() : getName();
      if (fileName.trim() === "") {
        setShowPopup(true);
        return;
      }

      const canvasDataURL = await store.toDataURL({
        pixelRatio: quality,
        mimeType: "image/png", // Ensure the mimeType is set to PNG
      });

      setImagePaths(prevPaths => [...prevPaths, canvasDataURL]);
      console.log("paths", imagePaths);
      alert("Image saved successfully!");

      // Construct your JSON data object
      const jsonData = store.toJSON();
      console.log("JSON Data:", jsonData);

      // Convert JSON data to Blob
      const jsonBlob = new Blob([JSON.stringify(jsonData)], { type: 'application/json' });

      // Convert canvas data URL to Blob
      const imageDataBlob = await fetch(canvasDataURL).then(res => res.blob());

      // Construct a FormData object
      const formData = new FormData();
      formData.append('title', finalFileName);
      formData.append('edited_by', userID);
      formData.append('templetepath', jsonBlob, finalFileName + '.json');
      formData.append('imagepath', imageDataBlob, finalFileName + '.png');

      const apiUrl = 'https://dev.maker4u.com/aapi/saveusertemplete.php';

      // Make the API call to save the user template
      const response = await fetch(API.getUrl(API.ENDPOINTS.SAVE_USER_TEMPLATE), {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Template saved successfully:', result);
        // Handle success
      } else {
        const result = await response.json();
        console.error('Failed to save the template:', result);
        // Handle error
        alert('Failed to save the template. Please try again.');
      }

    } catch (error) {
      console.error("Error saving image:", error);
      alert("Failed to save the image. Please try again.");
    }
  };

  const [selectedImage, setSelectedImage] = React.useState(null);

  // Function to handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    console.log("file", file);
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        console.log("reader.result", reader.result);
        setSelectedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  console.log("imagePaths:", imagePaths);

  const handlePayNow = async () => {
    const handleOpen = () => dispatch(loginModal(true));
    if (!isAuthenticated) {
      handleOpen();
      return;
    }
  
    const formData = new FormData();
    formData.append('tempid', temp_id);
  
    const apiUrl = 'https://dev.maker4u.com/aapi/gettemp_price.php';
  
    try {
      // Make the POST request
      const response = await fetch(API.getUrl(API.ENDPOINTS.GET_TEMPLATE_PRICE), {
        method: 'POST',
        body: formData,
      });
  
      // Ensure the response is OK
      if (!response.ok) {
        throw new Error('Failed to fetch template price');
      }
  
      // Parse the JSON response
      const data = await response.json();
  
      // Check if the response status is success and the amount exists
      if (data.status === "success" && data.temp_amount !== undefined) {
        const amount = data.temp_amount;
        console.log("Template Amount is: ", amount);
  
        // Construct the payment URL
        const url = `${API.API_URL}${API.ENDPOINTS.ONE_TIME_PAY}?amount=${encodeURIComponent(amount)}&customerEmail=${encodeURIComponent(email)}&userid=${encodeURIComponent(userID)}&tempid=${encodeURIComponent(packageid)}`;
  
        // Open the payment URL in a new tab
        window.open(url, '_blank');
      } else {
        console.error('Failed to retrieve template amount or invalid response:', data);
      }
    } catch (error) {
      console.error('Error processing payment:', error);
    }
  };
  

  return (
    <>
      <InputGroup
        type="text"
        placeholder="Enter file name"
        value={fileName}
        onChange={(e) => setFileName(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        style={{ marginRight: "10px", width: "200px", height: "25px", borderRadius: "12px", marginLeft: "10px" }}
        className="placeholder:pl-1  pl-2 focus:outline-none rounded-sm "
      />
      <button onClick={handleSave} style={{ color: "white", margin: "0px 10px 0 0" }} className={`hover:bg-[#ffffff0f] px-2  py-[5px] text-white hover:no-underline focus:outline-none active:outline-none rounded-sm  font-[500] `}>save</button>

      <Popover
        content={
          <Menu>
            <li className="bp5-menu-header">
              <h6 className="bp5-heading">File type</h6>
            </li>
            <HTMLSelect
              fill
              onChange={(e) => {
                setType(e.target.value);
                setQuality(1);
              }}
              value={type}
            >
              <option value="jpeg">JPEG</option>
              <option value="png">PNG</option>
              <option value="pdf">Download PDF</option>
              <option value="print">Print File</option>
              <option value="html">HTML</option>
              <option value="gif">GIF</option>
            </HTMLSelect>

            {type !== "html" && (
              <>
                <li className="bp5-menu-header">
                  <h6 className="bp5-heading">Quality</h6>
                </li>
                <div style={{ padding: "10px" }}>
                  <Slider
                    value={quality}
                    labelRenderer={false}
                    onChange={(quality) => {
                      setQuality(quality);
                    }}
                    stepSize={0.2}
                    min={0.2}
                    max={300 / 72}
                    showTrackFill={false}
                  />
                  {type === "pdf" || type === "print" && (
                    <div>DPI: {Math.round(store.dpi * quality)}</div>
                  )}
                  {type !== "pdf" && type !== "print" && (
                    <div>
                      {Math.round(store.width * quality)} x{" "}
                      {Math.round(store.height * quality)} px
                    </div>
                  )}

                  {type === "gif" && (
                    <>
                      <li className="bp5-menu-header">
                        <h6 className="bp5-heading">FPS</h6>
                      </li>
                      <div style={{ padding: "10px" }}>
                        <Slider
                          value={fps}
                          labelStepSize={5}
                          onChange={(fps) => {
                            setFPS(fps);
                          }}
                          stepSize={1}
                          min={5}
                          max={30}
                          showTrackFill={false}
                        />
                      </div>
                    </>
                  )}
                </div>
                {type === "pdf" || type === "print" && (
                  <>
                    <li className="bp5-menu-header">
                      <h6 className="bp5-heading">Page Size</h6>
                    </li>
                    <div style={{ padding: "10px" }}>
                      <Slider
                        value={pageSizeModifier}
                        labelRenderer={false}
                        onChange={(pageSizeModifier) => {
                          setPageSizeModifier(pageSizeModifier);
                        }}
                        stepSize={0.2}
                        min={0.2}
                        max={3}
                        showTrackFill={false}
                      />

                      <div>
                        {unit.pxToUnitRounded({
                          px: store.width * pageSizeModifier,
                          dpi: store.dpi,
                          precious: 0,
                          unit: "mm",
                        })}{" "}
                        x{" "}
                        {unit.pxToUnitRounded({
                          px: store.height * pageSizeModifier,
                          dpi: store.dpi,
                          precious: 0,
                          unit: "mm",
                        })}{" "}
                        mm
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
            {type === "html" && (
              <>
                <div style={{ padding: "10px", maxWidth: "180px", opacity: 0.8 }}>
                  HTML export is very experimental. If you have issues with it,
                  please report to Anton on discord.
                </div>
              </>
            )}
            <Button
              fill
              intent="primary"
              loading={saving}
              onClick={isAuthenticated ? handleDownload : <Button type="button"> Sign In </Button>}
              style={{ borderRadius: "5px", fontWeight: "bold", fontSize: "16px" }}
            >
              {type === "print" ? "Print File" : "DOWNLOAD " + type.toLocaleUpperCase()}
            </Button>
          </Menu>
        }
        position={Position.BOTTOM_RIGHT}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <Button
            icon="import"
            text={t("toolbar.download")}
            intent="primary"
            loading={saving}
            style={{ backgroundImage: "linear-gradient(to right, #5f619f  0%, #714e91  51%, #7e7399  100%)", filter: "contrast(60%)", marginRight: "10px", borderRadius: "5px", fontWeight: "bold", fontSize: "16px" }}
            onClick={() => {
              setQuality(1);
            }}
          />
        </div>
      </Popover>

      {/* Premium Template Popup */}
      <Dialog
        icon="warning-sign"
        onClose={() => setShowPremiumPopup(false)}
        title="Premium Template"
        isOpen={showPremiumPopup}
        style={{ width: '400px', borderRadius: '10px', padding: '20px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}
      >
        <div className={Classes.DIALOG_BODY} style={{ textAlign: 'center', paddingBottom: '20px' }}>
          <p style={{ fontSize: '16px', color: '#5c7080', marginBottom: '20px' }}>
            This is a premium template. Please upgrade your package to download or print it.
          </p>
          <FaCrown size={80} color="#FFD700" style={{ margin: '0 auto', display: 'block', marginBottom: '20px' }} />
        </div>
        <div className={Classes.DIALOG_FOOTER} style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
          <Button
            intent="primary"
            style={{
              backgroundColor: '#9333EA',
              color: 'white',
              fontWeight: 'bold',
              padding: '10px 20px',
              borderRadius: '5px',
            }}
            onClick={() => navigate('/price')}
          >
            Go to Pricing Page
          </Button>
          <Button
            style={{
              backgroundColor: '#F55656',
              color: 'white',
              fontWeight: 'bold',
              padding: '10px 20px',
              borderRadius: '5px',
            }}
            onClick={() => setShowPremiumPopup(false)}
          >
            Close
          </Button>
        </div>
      </Dialog>

      {/* One-Time Plan Template Popup */}
      <Dialog
        icon="warning-sign"
        onClose={() => setShowOneTimePlanPopup(false)}
        title="One-Time Plan Template"
        isOpen={showOneTimePlanPopup}
        style={{ width: '400px', borderRadius: '10px', padding: '20px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}
      >
        <div className={Classes.DIALOG_BODY} style={{ textAlign: 'center', paddingBottom: '20px' }}>
          <p style={{ fontSize: '16px', color: '#5c7080', marginBottom: '20px' }}>
            This template is only available in the one-time plan. Please go and purchase it!
          </p>
          <FaCrown size={80} color="#FFD700" style={{ margin: '0 auto', display: 'block', marginBottom: '20px' }} />
        </div>
        <div className={Classes.DIALOG_FOOTER} style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
          <Button
            intent="primary"
            style={{
              backgroundColor: '#9333EA',
              color: 'white',
              fontWeight: 'bold',
              padding: '10px 20px',
              borderRadius: '5px',
            }}
            onClick={handlePayNow}
          >
            Buy Now
          </Button>
          <Button
            style={{
              backgroundColor: '#F55656',
              color: 'white',
              fontWeight: 'bold',
              padding: '10px 20px',
              borderRadius: '5px',
            }}
            onClick={() => setShowOneTimePlanPopup(false)}
          >
            Close
          </Button>
        </div>
      </Dialog>

      <Dialog
        icon="info-sign"
        onClose={() => setShowPopup(false)}
        title="Enter File Name"
        isOpen={showPopup}
      >
        <div className={Classes.DIALOG_BODY}>
          <p>Please enter a file name before saving.</p>
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={() => setShowPopup(false)}>Close</Button>
          </div>
        </div>
      </Dialog>
      <Dialog
        icon="warning-sign"
        onClose={() => setShowExpiredPackagePopUp(false)}
        title="Package has been Expired"
        isOpen={showExpiredPackagePopUp}
        style={{ width: '400px', borderRadius: '10px', padding: '20px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}
      >
        <div className={Classes.DIALOG_BODY} style={{ textAlign: 'center', paddingBottom: '20px' }}>
          <p style={{ fontSize: '16px', color: '#5c7080', marginBottom: '20px' }}>
           Your Package has been Expired. Please go and Renew it!
          </p>
          <FaExclamationTriangle  size={80} color="#FFD700" style={{ margin: '0 auto', display: 'block', marginBottom: '20px' }} />
        </div>
        <div className={Classes.DIALOG_FOOTER} style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
          <Button
            intent="primary"
            style={{
              backgroundColor: '#9333EA',
              color: 'white',
              fontWeight: 'bold',
              padding: '10px 20px',
              borderRadius: '5px',
            }}
            onClick={() => navigate('/price')}
          >
            Renew
          </Button>
          <Button
            style={{
              backgroundColor: '#F55656',
              color: 'white',
              fontWeight: 'bold',
              padding: '10px 20px',
              borderRadius: '5px',
            }}
            onClick={() => setShowExpiredPackagePopUp(false)}
          >
            Close
          </Button>
        </div>
      </Dialog>
    </>
  );
});
